import { useCallback, useMemo, useState } from 'react'
import type {
  IntuiTableProps,
  PaginationConfig,
  SortState,
} from './types'

const DEFAULT_PAGE_SIZE = 10
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

function normalizePaginationConfig(
  pagination: boolean | PaginationConfig | undefined
): PaginationConfig | null {
  if (!pagination) return null
  if (pagination === true) {
    return {
      pageSize: DEFAULT_PAGE_SIZE,
      pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
      showPageSizeSelector: true,
      showPageInfo: true,
    }
  }
  return {
    pageSize: pagination.pageSize ?? DEFAULT_PAGE_SIZE,
    pageSizeOptions: pagination.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS,
    showPageSizeSelector: pagination.showPageSizeSelector ?? true,
    showPageInfo: pagination.showPageInfo ?? true,
  }
}

export function useIntuiTable<T>(props: IntuiTableProps<T>) {
  const {
    columns,
    data,
    getRowId,
    defaultSort,
    onSortChange,
    manualSorting = false,
    selectable = false,
    selectionMode = 'multi',
    selectedRows: controlledSelectedRows,
    onSelectionChange,
    isRowSelectable,
    pagination,
    totalRows,
    currentPage: controlledPage,
    onPageChange,
    onPageSizeChange,
    expandable = false,
    expandedRows: controlledExpandedRows,
    onExpandChange,
    columnVisibility,
  } = props

  // --- SORTING ---
  const [internalSort, setInternalSort] = useState<SortState | null>(
    defaultSort ?? null
  )
  const sortState = internalSort

  const handleSort = useCallback(
    (key: string) => {
      setInternalSort((prev) => {
        let next: SortState | null
        if (!prev || prev.key !== key) {
          next = { key, direction: 'asc' }
        } else if (prev.direction === 'asc') {
          next = { key, direction: 'desc' }
        } else {
          next = null
        }
        onSortChange?.(next)
        return next
      })
    },
    [onSortChange]
  )

  const sortedData = useMemo(() => {
    if (manualSorting || !sortState) return data
    const column = columns.find((c) => c.key === sortState.key)
    if (!column?.comparator) return data
    const sorted = [...data].sort(column.comparator)
    return sortState.direction === 'desc' ? sorted.reverse() : sorted
  }, [data, sortState, columns, manualSorting])

  // --- SELECTION ---
  const [internalSelectedIds, setInternalSelectedIds] = useState<Set<string>>(
    new Set()
  )
  const isControlledSelection = controlledSelectedRows !== undefined
  const selectedIds = isControlledSelection
    ? new Set(controlledSelectedRows)
    : internalSelectedIds

  const selectableRowIds = useMemo(() => {
    return new Set(
      data
        .filter((row) => (isRowSelectable ? isRowSelectable(row) : true))
        .map((row) => getRowId(row))
    )
  }, [data, getRowId, isRowSelectable])

  const updateSelection = useCallback(
    (newIds: Set<string>) => {
      if (!isControlledSelection) {
        setInternalSelectedIds(newIds)
      }
      onSelectionChange?.(Array.from(newIds))
    },
    [isControlledSelection, onSelectionChange]
  )

  const handleSelectRow = useCallback(
    (rowId: string) => {
      if (!selectableRowIds.has(rowId)) return

      if (selectionMode === 'single') {
        const newIds = selectedIds.has(rowId)
          ? new Set<string>()
          : new Set([rowId])
        updateSelection(newIds)
      } else {
        const newIds = new Set(selectedIds)
        if (newIds.has(rowId)) {
          newIds.delete(rowId)
        } else {
          newIds.add(rowId)
        }
        updateSelection(newIds)
      }
    },
    [selectedIds, selectionMode, selectableRowIds, updateSelection]
  )

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === selectableRowIds.size) {
      updateSelection(new Set())
    } else {
      updateSelection(new Set(selectableRowIds))
    }
  }, [selectedIds, selectableRowIds, updateSelection])

  const isAllSelected =
    selectableRowIds.size > 0 && selectedIds.size === selectableRowIds.size
  const isIndeterminate =
    selectedIds.size > 0 && selectedIds.size < selectableRowIds.size

  // --- PAGINATION ---
  const paginationConfig = normalizePaginationConfig(pagination)
  const [internalPage, setInternalPage] = useState(1)
  const [internalPageSize, setInternalPageSize] = useState(
    paginationConfig?.pageSize ?? DEFAULT_PAGE_SIZE
  )

  const isControlledPage = controlledPage !== undefined
  const page = isControlledPage ? controlledPage : internalPage
  const pageSize = internalPageSize

  const totalDataRows = totalRows ?? sortedData.length
  const totalPages = Math.max(1, Math.ceil(totalDataRows / pageSize))

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage < 1 || newPage > totalPages) return
      if (!isControlledPage) {
        setInternalPage(newPage)
      }
      onPageChange?.(newPage)
    },
    [isControlledPage, totalPages, onPageChange]
  )

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setInternalPageSize(newPageSize)
      // Reset to page 1 when page size changes
      if (!isControlledPage) {
        setInternalPage(1)
      }
      onPageSizeChange?.(newPageSize)
      onPageChange?.(1)
    },
    [isControlledPage, onPageSizeChange, onPageChange]
  )

  const paginatedData = useMemo(() => {
    if (!paginationConfig) return sortedData
    // Server-side pagination: don't slice, data is already paginated
    if (totalRows !== undefined) return sortedData
    const start = (page - 1) * pageSize
    return sortedData.slice(start, start + pageSize)
  }, [sortedData, paginationConfig, page, pageSize, totalRows])

  // --- EXPAND ---
  const [internalExpandedIds, setInternalExpandedIds] = useState<Set<string>>(
    new Set()
  )
  const isControlledExpand = controlledExpandedRows !== undefined
  const expandedIds = isControlledExpand
    ? new Set(controlledExpandedRows)
    : internalExpandedIds

  const handleToggleExpand = useCallback(
    (rowId: string) => {
      const newIds = new Set(expandedIds)
      if (newIds.has(rowId)) {
        newIds.delete(rowId)
      } else {
        newIds.add(rowId)
      }
      if (!isControlledExpand) {
        setInternalExpandedIds(newIds)
      }
      onExpandChange?.(Array.from(newIds))
    },
    [expandedIds, isControlledExpand, onExpandChange]
  )

  // --- VISIBLE COLUMNS ---
  const visibleColumns = useMemo(() => {
    return columns.filter((col) => {
      if (col.hidden) return false
      if (columnVisibility && columnVisibility[col.key] === false) return false
      return true
    })
  }, [columns, columnVisibility])

  // Total column count for colSpan calculations
  const totalColumnCount = useMemo(() => {
    let count = visibleColumns.length
    if (selectable) count += 1
    if (expandable) count += 1
    if (props.rowActions?.length) count += 1
    return count
  }, [visibleColumns.length, selectable, expandable, props.rowActions])

  return {
    // Sort
    sortState,
    handleSort,
    sortedData,
    // Selection
    selectedIds,
    handleSelectRow,
    handleSelectAll,
    isAllSelected,
    isIndeterminate,
    // Pagination
    paginationConfig,
    page,
    pageSize,
    paginatedData,
    totalPages,
    totalDataRows,
    handlePageChange,
    handlePageSizeChange,
    // Expand
    expandedIds,
    handleToggleExpand,
    // Columns
    visibleColumns,
    totalColumnCount,
  }
}
